import {
  INVOICE_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  type Invoice,
} from '@faktorplus/shared';
import { managerLabel, projectLabel } from './invoice-labels';

const COLUMNS = [
  'Numer faktury',
  'Pozycja księgowa',
  'Data',
  'Kontrahent',
  'Budowa',
  'Kierownik',
  'Pracownicy',
  'Kategoria',
  'Kwota netto',
  'Kwota VAT',
  'Kwota brutto',
  'Zatrzymanie',
  'Do zapłaty',
  'Wpłacono',
  'Pozostało',
  'Termin płatności',
  'Forma płatności',
  'Status',
  'Data zapłaty',
];

/**
 * CSV export of the current view.
 *
 * Not a nicety: the client has run the business out of Excel for years, and a
 * visible way back to a spreadsheet is what makes giving up the spreadsheet
 * acceptable. Semicolons and comma decimals, because that is what Polish Excel
 * opens without an import wizard.
 */
export function exportInvoicesToCsv(invoices: Invoice[], filename = buildFilename()): void {
  const rows = invoices.map((invoice) => [
    invoice.number,
    invoice.ledgerRef ?? '',
    formatDate(invoice.issueDate),
    invoice.vendorName,
    projectLabel(invoice).text.replace('— ', ''),
    managerLabel(invoice),
    invoice.employees.map((employee) => employee.fullName).join(', '),
    invoice.categoryName ?? '',
    formatAmount(invoice.netAmount),
    formatAmount(invoice.vatAmount),
    formatAmount(invoice.grossAmount),
    formatAmount(invoice.retentionAmount),
    formatAmount(invoice.amountDue),
    formatAmount(invoice.paidAmount),
    formatAmount(invoice.remainingAmount),
    formatDate(invoice.dueDate),
    PAYMENT_METHOD_LABELS[invoice.paymentMethod],
    INVOICE_STATUS_LABELS[invoice.status],
    formatDate(invoice.paidAt),
  ]);

  const csv = [COLUMNS, ...rows].map((row) => row.map(escapeCell).join(';')).join('\r\n');

  // The BOM makes Excel read the file as UTF-8 and keeps Polish characters intact.
  downloadFile(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }), filename);
}

function escapeCell(value: string): string {
  return /[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function formatAmount(amount: number): string {
  return amount.toFixed(2).replace('.', ',');
}

function formatDate(value: Date | string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('pl-PL');
}

function buildFilename(): string {
  return `faktury-${new Date().toISOString().slice(0, 10)}.csv`;
}

function downloadFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}
